import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Loader2 } from "lucide-react";
import { Page } from "@/components/layout";
import { ContentCard } from "@/components/ui/ContentCard";
import { getDistrictRoster, type DistrictRoster } from "@/services/teamsService";
import {
  SAMPLE_DISTRICT_NAME,
  SAMPLE_DISTRICT_SCHOOLS,
  type DistrictSchoolSummary,
} from "@/config/teamsSample";
import { cn } from "@/lib/utils";
import { InstitutionalContextDocumentsCard } from "./InstitutionalContextDocumentsCard";

export type DistrictSchoolsViewMode = "district" | "teams";

interface DistrictSchoolsPageProps {
  /** When provided, fetches schools for that district; otherwise the sample roster shows. */
  districtId?: string | null;
  /** `teams` — district_leader copy; `district` — superintendent / super-admin copy. */
  viewMode?: DistrictSchoolsViewMode;
  canManageOrganizationContext: boolean;
}

const PAGE_COPY: Record<
  DistrictSchoolsViewMode,
  { title: string; subtitle: (districtName: string) => string }
> = {
  teams: {
    title: "My Teams",
    subtitle: (name) =>
      `${name} · pick a school to see aggregate patterns for that building — individual sessions stay private.`,
  },
  district: {
    title: "My District",
    subtitle: (name) =>
      `${name} · pick a school to see what its staff is reaching for this semester.`,
  },
};

export const DistrictSchoolsPage = ({
  districtId,
  viewMode = "district",
  canManageOrganizationContext,
}: DistrictSchoolsPageProps) => {
  const copy = PAGE_COPY[viewMode];
  const [roster, setRoster] = useState<DistrictRoster>({
    districtName: SAMPLE_DISTRICT_NAME,
    schools: SAMPLE_DISTRICT_SCHOOLS,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    void getDistrictRoster(districtId ?? null)
      .then((next) => {
        if (!cancelled) setRoster(next);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [districtId]);

  return (
    <Page.Container>
      <Page.Header className="space-y-1">
        <h1 className="font-literata text-3xl font-semibold text-white md:text-4xl">{copy.title}</h1>
        <p className="max-w-2xl font-inter text-sm leading-relaxed text-white/80 md:text-base">
          {copy.subtitle(roster.districtName)}
        </p>
      </Page.Header>

      <Page.Content className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" aria-hidden />
          </div>
        ) : (
          <>
            <InstitutionalContextDocumentsCard
              variant="district"
              scopeKey={districtId ?? "sample-district"}
              entityLabel={roster.districtName}
              canManage={canManageOrganizationContext}
            />
            <ContentCard className="p-0">
              <ul className="divide-y divide-stone-200">
                {roster.schools.map((school) => (
                  <li key={school.schoolId}>
                    <SchoolRow school={school} />
                  </li>
                ))}
                {roster.schools.length === 0 && (
                  <li className="px-5 py-6 text-center font-inter text-sm text-stone-500">
                    No schools yet.
                  </li>
                )}
              </ul>
            </ContentCard>
          </>
        )}
      </Page.Content>
    </Page.Container>
  );
};

const SchoolRow = ({ school }: { school: DistrictSchoolSummary }) => (
  <Link
    to={`/admin/my-teams/${encodeURIComponent(school.schoolId)}`}
    className="group flex items-center gap-4 px-4 py-4 transition-colors hover:bg-stone-50 md:px-6 md:py-5"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
      <Building2 className="h-5 w-5" aria-hidden />
    </div>

    <div className="min-w-0 flex-1">
      <p className="font-literata text-base font-semibold text-stone-900 md:text-lg">{school.schoolName}</p>
      <p className="font-inter text-xs text-stone-500 md:text-sm">
        {school.semesterLabel}
        <span aria-hidden className="mx-1.5 text-stone-300">
          ·
        </span>
        {school.helperCount} {school.helperCount === 1 ? "helper" : "helpers"}
      </p>
      <SchoolAggregateStatus school={school} />
      {school.headlineNote && school.hasAggregateSignal && (
        <p className="mt-1 font-inter text-xs italic text-stone-500 md:text-sm line-clamp-2">
          {school.headlineNote}
        </p>
      )}
    </div>

    <div className="flex shrink-0 flex-col items-end gap-1">
      <span className="font-inter text-[10px] font-medium uppercase tracking-wide text-stone-400 group-hover:text-stone-600">
        View patterns
      </span>
      <ArrowRight
        aria-hidden
        className="h-4 w-4 text-stone-400 transition-transform group-hover:translate-x-0.5 group-hover:text-stone-600"
      />
    </div>
  </Link>
);

const SchoolAggregateStatus = ({ school }: { school: DistrictSchoolSummary }) => {
  if (school.hasAggregateSignal) {
    return (
      <p className="mt-1.5 font-inter text-xs text-emerald-800 md:text-sm">
        <span
          className={cn(
            "mr-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            "bg-emerald-100 text-emerald-800",
          )}
        >
          {school.focusAreaCount} focus {school.focusAreaCount === 1 ? "area" : "areas"}
        </span>
        Aggregate patterns available
      </p>
    );
  }

  if (school.helperCount > 0) {
    return (
      <p className="mt-1.5 font-inter text-xs text-amber-800 md:text-sm">
        Usage building — patterns appear once categories reach the 25% threshold
      </p>
    );
  }

  return (
    <p className="mt-1.5 font-inter text-xs text-stone-500 md:text-sm">
      No staff usage yet — open to see an example of what this page will look like
    </p>
  );
};
